import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";

export const SCRIPTWRITER_STATUSES = [
  "work_in_progress",
  "completed",
  "waiting_for_approval",
] as const;
export const SCRIPTWRITER_BRANDS = ["Brand", "Viral"] as const;
export const SCRIPTWRITER_CORRECTION_STATUSES = [
  "pending",
  "approved",
  "fix",
] as const;
// SOURCE OF TRUTH: also maintained in lookup_items
// table. When adding new values, add to BOTH here
// and lookup_items, or the DB will accept it but
// the dropdown won't show it.
export const SCRIPTWRITER_OTHER_TASK_TYPES = [
  "research",
  "meetings",
  "software_error",
  "laptop_issue",
  "fun_friday",
  "others",
] as const;

export type ScriptwriterStatus = (typeof SCRIPTWRITER_STATUSES)[number];
export type ScriptwriterBrand = (typeof SCRIPTWRITER_BRANDS)[number];
export type ScriptwriterCorrectionStatus =
  (typeof SCRIPTWRITER_CORRECTION_STATUSES)[number];
export type ScriptwriterOtherTaskType =
  (typeof SCRIPTWRITER_OTHER_TASK_TYPES)[number];

export const SCRIPTWRITER_STATUS_LABEL: Record<ScriptwriterStatus, string> = {
  work_in_progress: "Work in Progress",
  completed: "Completed",
  waiting_for_approval: "Waiting for Approval",
};
export const SCRIPTWRITER_CORRECTION_LABEL: Record<
  ScriptwriterCorrectionStatus,
  string
> = { pending: "Pending", approved: "Approved", fix: "Fix" };
export const SCRIPTWRITER_OTHER_TASK_LABEL: Record<
  ScriptwriterOtherTaskType,
  string
> = {
  research: "Research",
  meetings: "Meetings",
  software_error: "Software Error",
  laptop_issue: "Laptop Issue",
  fun_friday: "Fun Friday / Celebration",
  others: "Others",
};

export interface ScriptwriterSubmission {
  id: string;
  user_id: string;
  script_title: string;
  product_name: string;
  brand: ScriptwriterBrand;
  status: ScriptwriterStatus;
  drive_link: string;
  submission_date: string;
  notes: string | null;
  correction_status: ScriptwriterCorrectionStatus;
  correction_note: string | null;
  points: number;
  is_winner: boolean;
  created_at: string;
  updated_at: string;
}

export interface ScriptwriterOtherTask {
  id: string;
  user_id: string;
  task_date: string;
  start_time: string;
  end_time: string;
  task_type: ScriptwriterOtherTaskType;
  notes: string;
  created_at: string;
  updated_at: string;
}

// Schemas
export const scriptwriterSubmissionSchema = z.object({
  script_title: z.string().trim().min(1, "Required").max(200),
  product_name: z.string().trim().min(1, "Required").max(200),
  brand: z.enum(SCRIPTWRITER_BRANDS, { required_error: "Pick one" }),
  status: z.enum(SCRIPTWRITER_STATUSES),
  drive_link: z.string().url("Must be a valid URL"),
  notes: z.string().max(500).optional().or(z.literal("")),
});
export type ScriptwriterSubmissionInput = z.infer<
  typeof scriptwriterSubmissionSchema
>;

export const scriptwriterOtherTaskSchema = z
  .object({
    start_time: z.string().regex(/^\d{2}:\d{2}$/, "Required"),
    end_time: z.string().regex(/^\d{2}:\d{2}$/, "Required"),
    task_type: z.enum(SCRIPTWRITER_OTHER_TASK_TYPES, {
      required_error: "Required",
    }),
    notes: z.string().trim().min(1, "Required").max(1000),
  })
  .refine((d) => d.end_time > d.start_time, {
    message: "End time must be after start time",
    path: ["end_time"],
  });
export type ScriptwriterOtherTaskInput = z.infer<
  typeof scriptwriterOtherTaskSchema
>;

export const scriptwriterBulkRowSchema = z.object({
  script_title: z.string().trim().min(1).max(200),
  product_name: z.string().trim().min(1).max(200),
  brand: z.enum(SCRIPTWRITER_BRANDS),
  status: z.enum(SCRIPTWRITER_STATUSES),
  drive_link: z.string().url(),
});
export type ScriptwriterBulkRow = z.infer<typeof scriptwriterBulkRowSchema>;

// Queries
export async function fetchMyScriptwriterSubmissions(userId: string) {
  const { data, error } = await supabase
    .from("scriptwriter_submissions")
    .select("*")
    .eq("user_id", userId)
    .is("deleted_at", null)
    .order("submission_date", { ascending: false })
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as ScriptwriterSubmission[];
}

export async function fetchMyScriptwriterOtherTasks(userId: string) {
  const { data, error } = await supabase
    .from("scriptwriter_other_tasks")
    .select("*")
    .eq("user_id", userId)
    .is("deleted_at", null)
    .order("task_date", { ascending: false })
    .order("start_time", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as ScriptwriterOtherTask[];
}

export async function fetchWinners() {
  const { data, error } = await supabase
    .from("scriptwriter_submissions")
    .select("*")
    .eq("is_winner", true)
    .is("deleted_at", null)
    .order("submission_date", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as ScriptwriterSubmission[];
}

export async function insertScriptwriterSubmission(
  userId: string,
  input: ScriptwriterSubmissionInput,
) {
  const { data, error } = await supabase
    .from("scriptwriter_submissions")
    .insert({
      user_id: userId,
      script_title: input.script_title,
      product_name: input.product_name,
      brand: input.brand,
      status: input.status,
      drive_link: input.drive_link,
      notes: input.notes || null,
    })
    .select()
    .single();
  if (error) throw error;
  return data as unknown as ScriptwriterSubmission;
}

export async function updateScriptwriterSubmission(
  id: string,
  input: ScriptwriterSubmissionInput,
) {
  const { data, error } = await supabase
    .from("scriptwriter_submissions")
    .update({
      script_title: input.script_title,
      product_name: input.product_name,
      brand: input.brand,
      status: input.status,
      drive_link: input.drive_link,
      notes: input.notes || null,
    })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as ScriptwriterSubmission;
}

export async function deleteScriptwriterSubmission(id: string) {
  const { error } = await supabase
    .from("scriptwriter_submissions")
    .delete()
    .eq("id", id);
  if (error) throw error;
}

export async function setScriptwriterWinner(id: string, isWinner: boolean) {
  const { data, error } = await supabase
    .from("scriptwriter_submissions")
    .update({ is_winner: isWinner })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as ScriptwriterSubmission;
}

export async function bulkInsertScriptwriterSubmissions(
  rows: ScriptwriterBulkRow[],
) {
  const payload = rows;
  const { data, error } = await supabase.rpc(
    "bulk_insert_scriptwriter_submissions",
    { rows: payload as never },
  );
  if (error) throw error;
  return data as unknown as ScriptwriterSubmission[];
}

export async function insertScriptwriterOtherTask(
  userId: string,
  input: ScriptwriterOtherTaskInput,
) {
  const { data, error } = await supabase
    .from("scriptwriter_other_tasks")
    .insert({
      user_id: userId,
      start_time: input.start_time,
      end_time: input.end_time,
      task_type: input.task_type,
      notes: input.notes,
    })
    .select()
    .single();
  if (error) throw error;
  return data as unknown as ScriptwriterOtherTask;
}

export async function updateScriptwriterOtherTask(
  id: string,
  input: ScriptwriterOtherTaskInput,
) {
  const { data, error } = await supabase
    .from("scriptwriter_other_tasks")
    .update({
      start_time: input.start_time,
      end_time: input.end_time,
      task_type: input.task_type,
      notes: input.notes,
    })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as ScriptwriterOtherTask;
}

export async function deleteScriptwriterOtherTask(id: string) {
  const { error } = await supabase
    .from("scriptwriter_other_tasks")
    .delete()
    .eq("id", id);
  if (error) throw error;
}

export function isCurrentMonth(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  return (
    d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
  );
}
